import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../index";

export interface Permission {
  type: string;
  used: number | null;
  limit: number | null;
  details: any[];
  permission_id: number;
}

// A single pitch pack purchase. Credits are tied to the pack they were bought
// with and are consumed FIFO (oldest first), so a user can have several active
// packs at once — each with its own expiry.
export interface PitchPack {
  id: number;
  packageName: string;
  pitchLimit: number;
  expiresAt: string;
  purchaseDate: string;
  isExpired: boolean;
}

export interface Purchase {
  id: number;
  email: string;
  productId: string;
  status: string;
  transactionId: string;
  // Aggregated across all active packs: total remaining credits.
  pitchLimit: number;
  userId: number;
  // Newest active pack's name (kept for backward compatibility).
  packageName: string;
  // Earliest upcoming expiry among active packs.
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  isExpired?: boolean;
  // Per-pack breakdown (active packs only), oldest first. Drives the packs table.
  packs?: PitchPack[];
}

interface UserState {
  id: number | null;
  fullName: string | null;
  email: string | null;
  role: string | null;
  userType: string | null;
  labelId: number | null;
  createdAt: string | null;
  lastLogin: string | null;
  profilePicture: string | null;
  Label: {
    name: string;
    type?: string;
    availableDSPs: string[];
    availableAnalyticsDSPs: string[];
  } | null;
  organization: {
    id: string;
    name: string;
  } | null;
  permissions: Permission[];
  purchase: Purchase | null;
  subscriptionStatus: string | null;
  hasDismissedDraftPitchReminder: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  id: null,
  fullName: null,
  email: null,
  role: null,
  userType: null,
  labelId: null,
  createdAt: null,
  lastLogin: null,
  profilePicture: null,
  Label: null,
  organization: null,
  permissions: [],
  purchase: null,
  subscriptionStatus: null,
  hasDismissedDraftPitchReminder: false,
  loading: false,
  error: null,
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<Partial<UserState>>) => {
      return {
        ...state,
        ...action.payload,
        loading: false,
        error: null,
      };
    },
    setUserType: (state, action: PayloadAction<string>) => {
      state.userType = action.payload;
    },
    setSubscriptionStatus: (state, action: PayloadAction<string | null>) => {
      state.subscriptionStatus = action.payload;
    },
    clearUser: () => {
      return initialState;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
});

export const { setUser, setUserType, setSubscriptionStatus, clearUser, setLoading, setError } =
  userSlice.actions;

// Selectors
export const selectUser = (state: RootState) => state.user;
export const selectUserType = (state: RootState) => state.user.userType;
export const selectCompanyType = (state: RootState) => state.user.Label?.type;
export const selectUserPermissions = (state: RootState) => state.user.permissions;
export const selectUserPurchase = (state: RootState) => state.user.purchase;
export const selectSubscriptionStatus = (state: RootState) => state.user.subscriptionStatus;

// Permission selectors by type
export const selectPermissionByType = (state: RootState, type: string) => {
  const permission = state.user.permissions.find(p => p.type === type);
  return permission;
};

export const selectAimsPermission = (state: RootState) =>
  selectPermissionByType(state, 'aims');

export const selectHeadlessPermission = (state: RootState) =>
  selectPermissionByType(state, 'headless');

export const selectPitchesPermission = (state: RootState) =>
  selectPermissionByType(state, 'pitches');

export const selectRoasterPermission = (state: RootState) =>
  selectPermissionByType(state, 'roaster');

export const selectEmailPermission = (state: RootState) =>
  selectPermissionByType(state, 'email');

export default userSlice.reducer;
